import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Trash2, Truck, Home, Hammer, Building,
  Package, Check, ArrowRight, Phone, Sofa,
  TreePine, Warehouse
} from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { serviceSchema, offerSchema, faqSchema } from "@/utils/schemas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Home, Truck, Building, Warehouse, Package,
  Hammer, Trash2, Sofa, TreePine,
};

const ServicesSkeleton = () => (
  <div data-testid="services-page" className="pt-20">
    <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-pulse">
          <div className="h-4 w-24 bg-white/20 rounded mb-4"></div>
          <div className="h-16 w-96 bg-white/20 rounded mb-6"></div>
          <div className="h-6 w-80 bg-white/20 rounded"></div>
        </div>
      </div>
    </section>
  </div>
);

export default function Services() {
  const { formatted_phone, phone_link } = useSiteConfig();
  const [cms, setCms] = useState(null);
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [cta, setCta] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, cmsRes] = await Promise.all([
          axios.get(`${API}/cms/services`),
          axios.get(`${API}/cms/page/services`),
        ]);

        setCategories(servicesRes.data.categories || []);
        setPricing(servicesRes.data.pricing || {});
        setCta(servicesRes.data.cta || {});

        // Hero from existing CMS system
        const heroSection = cmsRes.data?.find(s => s.section === "hero");
        setCms({
          hero: heroSection?.content || {
            headline1: "Professional",
            headline2: "Junk Solutions",
            description: "From single-item pickups to complete property clean outs, we have the equipment and expertise to handle any job in the DFW metroplex.",
          },
        });
      } catch {
        setCms({
          hero: {
            headline1: "Professional",
            headline2: "Junk Solutions",
            description: "From single-item pickups to complete property clean outs, we have the equipment and expertise to handle any job in the DFW metroplex.",
          },
        });
      } finally {
        setLoaded(true);
      }
    };
    fetchData();
  }, []);

  const getIcon = (iconName) => ICON_MAP[iconName] || Package;

  const servicesSchemas = useMemo(() => {
    const s = [];
    categories.forEach(cat => {
      s.push(serviceSchema({ name: cat.title, description: cat.description, image: cat.image }));
    });
    s.push(offerSchema("Free, no-obligation junk removal quote"));
    const faqs = categories.map(cat => ({
      question: `What ${cat.title.toLowerCase()} does Overhaul Ops offer?`,
      answer: `${cat.description} Our ${cat.title.toLowerCase()} include: ${(cat.services || []).map(s => s.name).join(", ")}.`,
    }));
    if (faqs.length > 0) s.push(faqSchema(faqs));
    return s;
  }, [categories]);

  if (!loaded) return <ServicesSkeleton />;

  return (
    <div data-testid="services-page" className="pt-20">
      <SEOHead
        title="Professional Junk Removal Services"
        description="Complete junk removal, hauling, cleanout, and light demolition services for homes and businesses in the DFW metroplex."
        keywords="junk removal services, residential junk removal, commercial cleanout, estate cleanout, furniture removal, appliance removal DFW"
        schemas={servicesSchemas}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Services", url: "/services" }]}
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              Our Services
            </span>
            <h1
              className="text-5xl md:text-7xl font-black text-white uppercase tracking-wide leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {cms?.hero?.headline1}<br />
              <span className="text-[#b51a00]">{cms?.hero?.headline2}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {cms?.hero?.description}
            </p>
          </div>
        </div>
      </section>

      {/* Dynamic Service Categories */}
      {categories.map((cat, catIndex) => {
        const IconComponent = getIcon(cat.icon);
        return (
          <section
            key={cat.slug || catIndex}
            id={cat.slug}
            data-testid={`service-category-${cat.slug}`}
            className={`py-20 md:py-28 ${catIndex % 2 === 0 ? "bg-white" : "bg-[#f4f4f5]"}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16 ${catIndex % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
                <div className={catIndex % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative">
                    {cat.image && (
                      <img
                        src={cat.image.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${cat.image}` : cat.image}
                        alt={cat.title}
                        className="w-full rounded-sm shadow-2xl"
                        width="600"
                        height="400"
                        loading="lazy"
                      />
                    )}
                    <div className="absolute -bottom-4 -right-4 bg-[#b51a00] p-4 rounded-sm shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                <div className={catIndex % 2 === 1 ? "lg:order-1" : ""}>
                  <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                    {String(catIndex + 1).padStart(2, '0')} — Services
                  </span>
                  <h2
                    className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 mb-6 text-[#0f0f0f]"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {cat.title}
                  </h2>
                  <div className="section-divider mb-6"></div>
                  <p className="text-lg text-[#4a4a4a] leading-relaxed">
                    {cat.description}
                  </p>
                </div>
              </div>

              <div className={`grid grid-cols-1 md:grid-cols-2 ${cat.services?.length > 2 ? "lg:grid-cols-3" : ""} gap-6`}>
                {(cat.services || []).map((service, sIndex) => (
                  <div
                    key={sIndex}
                    data-testid={`service-item-${cat.slug}-${sIndex}`}
                    className="bg-white border border-gray-200 hover:border-[#b51a00] rounded-sm p-6 transition-all hover:shadow-lg group"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#b51a00]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#b51a00] transition-colors">
                        <Check className="w-4 h-4 text-[#b51a00] group-hover:text-white transition-colors" />
                      </div>
                      <h3
                        className="text-xl font-bold uppercase tracking-tight text-[#0f0f0f]"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {service.name}
                      </h3>
                    </div>
                    <p className="text-[#4a4a4a] text-sm leading-relaxed pl-11">
                      {service.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10 text-center">
                <Link to="/contact">
                  <Button
                    data-testid={`service-cta-${cat.slug}`}
                    className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-4"
                  >
                    Get a Free Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        );
      })}

      {/* Pricing Info - Dynamic */}
      {pricing && (
        <section data-testid="pricing-info" className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                  {pricing.headline_label || "Transparent Pricing"}
                </span>
                <h2
                  className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 mb-6 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {pricing.headline || "Fair & Upfront"}
                </h2>
                <div className="section-divider mb-8"></div>
                <p className="text-lg text-[#4a4a4a] leading-relaxed mb-6">
                  {pricing.description}
                </p>
                <div className="bg-[#f4f4f5] p-6 rounded-sm mb-8">
                  <h4
                    className="font-bold text-lg mb-4 text-[#0f0f0f]"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    ACCEPTED PAYMENT METHODS
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {(pricing.payment_methods || []).map((method) => (
                      <span
                        key={method}
                        className="bg-white px-4 py-2 rounded-sm text-sm font-medium text-[#4a4a4a] border"
                      >
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-[#1a1a1a] p-8 rounded-sm text-white noise-texture">
                <h3
                  className="text-2xl font-bold uppercase tracking-tight mb-6"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Get Your Free Quote
                </h3>
                <ul className="space-y-4 mb-8">
                  {(pricing.quote_benefits || []).map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#FFD700]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-3">
                  <Link to="/contact" className="block">
                    <Button className="w-full bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm py-4">
                      Request Quote Online
                    </Button>
                  </Link>
                  <a href={phone_link} className="block">
                    <Button
                      variant="outline"
                      className="w-full border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] font-bold uppercase tracking-wider rounded-sm py-4"
                    >
                      <Phone className="mr-2 w-4 h-4" />
                      {formatted_phone}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Dynamic */}
      {cta && (
        <section className="py-20 bg-[#b51a00]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {cta.headline}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {cta.description}
            </p>
            <Link to="/contact">
              <Button
                data-testid="services-cta-btn"
                className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
              >
                {cta.button_text || "Get Your Free Quote"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
